<template>
  <VMenu
    :model-value="showDropdownValue"
    :close-on-content-click="false"
    :close-on-back="false"
    :open-on-click="false"
  >
    <template #activator="{ props: vMenuProps }">
      <slot
        :props="vMenuProps"
        :menu-el="easyTimePickerDropdown"
      />
    </template>

    <div
      ref="easyTimePickerDropdown"
      class="easy-time-picker__dropdown"
    >
      <div class="easy-time-picker__dropdown--sliders">
        <EasyStepSlider
          v-model="hours"
          class="easy-time-picker__dropdown--sliders--hours"
          :class="{ 'slider-half': !useMeridiem }"
          :options="timeEntryUtils.getHoursOptions(useMeridiem)"
        />
        <EasyStepSlider
          v-model="minutes"
          class="easy-time-picker__dropdown--sliders--minutes"
          :class="{ 'slider-half': !useMeridiem }"
          :options="timeEntryUtils.minutesOptions"
        />
        <EasyStepSlider
          v-if="useMeridiem"
          v-model="meridiem"
          class="easy-time-picker__dropdown--sliders--meridiem"
          :options="['am', 'pm']"
        />
      </div>

      <button
        ref="easyTimePickerNowButton"
        class="button-positive button-outlined justify-center"
        @click="setNow"
      >
        {{ t("date_picker.label_now") }}
      </button>
    </div>
  </VMenu>
</template>

<script setup lang="ts">
import EasyStepSlider from "../EasyStepSlider.vue";
import timeEntryUtils from "@/src/shared/utils/timeEntry";
import { EasyTimePickerDropdownEmits, EasyTimePickerDropdownProps, TimeParts } from "../../types/easyTimePicker";
import { MAX_HOURS_TIME_INPUT_24H } from "@/src/shared/constants/timeEntry";
import { VMenu } from "vuetify/components/VMenu";
import { computed, ref } from "vue";
import { useGetSetHelpers } from "../../composables/easyTimePicker/getSetHelpers";

const props = withDefaults(defineProps<EasyTimePickerDropdownProps>(), {
  useMeridiem: false,
  maxHours: MAX_HOURS_TIME_INPUT_24H,
});
const emit = defineEmits<EasyTimePickerDropdownEmits>();

const { setInputValue } = useGetSetHelpers(props, emit);
const hours = computed({
  get: () => props.hoursValue,
  set: hours => setInputValue(TimeParts.Hours, hours || ""),
});
const minutes = computed({
  get: () => props.minutesValue,
  set: minutes => setInputValue(TimeParts.Minutes, minutes || ""),
});
const meridiem = computed({
  get: () => props.meridiemValue,
  set: meridiem => setInputValue(TimeParts.Meridiem, meridiem || ""),
});

const easyTimePickerDropdown = ref<HTMLDivElement>();
const easyTimePickerNowButton = ref<HTMLDivElement>();

const t = window.EasyLocale.getLocale;

const setNow = () => {
  const currentHHmm = timeEntryUtils.getCurrentHHmm();
  const timeParts = timeEntryUtils.extractTimePartsFromValue(currentHHmm, props.useMeridiem);
  hours.value = timeParts.hours;
  minutes.value = timeParts.minutes;
  meridiem.value = timeParts.meridiem;
  easyTimePickerNowButton.value?.blur();
};
</script>
